import { useMutation, useQueryClient } from '@tanstack/react-query';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import { LeadDetailsResponse, StatusChangeNotePayload } from '../types';
import { LEAD_DETAILS } from './constants';

// HWM-22633 round 4: edit / clear the `status_change_note` on a single
// historical PatientLead row. Server-confirmed cache patch — locate the
// matching `status_change` entry in `[LEAD_DETAILS, leadId].activity_log`
// and replace `note` + `note_date_time` from the server response. The
// inline editor sits next to its own row in the timeline, so the cache
// patch IS the visible feedback; no toast (would spam when an agent
// annotates several past status changes in a row).
const useUpdateStatusChangeNote = (onSuccess?: (data: StatusChangeNotePayload) => void) => {
    const req = useRequest();
    const queryClient = useQueryClient();

    const request = ({
        leadId,
        historicalId,
        note,
        noteDateTime
    }: {
        leadId: string;
        historicalId: number;
        note: string | null;
        noteDateTime?: string | null;
    }): Promise<StatusChangeNotePayload> => {
        const body: { note: string | null; note_date_time?: string } = { note };
        if (noteDateTime) body.note_date_time = noteDateTime;
        return req({
            url: `${envVars.API_RTM_HOSTNAME}enterprise/leads/${leadId}/status-changes/${historicalId}/note/`,
            method: 'PATCH',
            body: JSON.stringify(body)
        }) as Promise<StatusChangeNotePayload>;
    };

    return useMutation({
        mutationFn: request,
        onSuccess: (data, vars) => {
            queryClient.setQueryData<LeadDetailsResponse | undefined>(
                [LEAD_DETAILS, vars.leadId],
                (old) => {
                    if (!old?.activity_log) return old;
                    return {
                        ...old,
                        activity_log: old.activity_log.map((entry) =>
                            entry.kind === 'status_change' && entry.id === vars.historicalId
                                ? { ...entry, note: data.note, note_date_time: data.note_date_time }
                                : entry
                        )
                    };
                }
            );
            onSuccess && onSuccess(data);
        },
        onError: (error: { errors: string[] }) => {
            return error;
        }
    });
};

export default useUpdateStatusChangeNote;
